import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ChevronLeft, Upload, FileText, X, Sparkles, Clock, CalendarDays } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { useLang } from "../context/LanguageContext";
import TopBar from "../components/TopBar";

export default function StudyPlanCreate() {
  const navigate = useNavigate();
  const { t, lang } = useLang();
  const [title, setTitle] = useState("");
  const [text, setText] = useState("");
  const [days, setDays] = useState(7);
  const [hours, setHours] = useState(2);
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);

  const onFiles = (e) => setFiles([...files, ...Array.from(e.target.files)]);
  const removeFile = (i) => setFiles(files.filter((_, idx) => idx !== i));

  const submit = async () => {
    if (!text.trim() && files.length === 0) { toast.error(t("Aggiungi del testo o carica un file")); return; }
    setBusy(true);
    const fd = new FormData();
    fd.append("title", title);
    fd.append("pasted_text", text);
    fd.append("days", days);
    fd.append("hours_per_day", hours);
    fd.append("lang", lang);
    files.forEach((f) => fd.append("files", f));
    try {
      const { data } = await api.post("/plans", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(t("Piano di studio creato! 🚀"));
      navigate(`/plan/${data.plan_id}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <div className="space-bg min-h-screen pb-20">
      <TopBar />
      <div className="max-w-lg mx-auto px-4 pt-5">
        <button onClick={() => navigate("/dashboard")} className="flex items-center gap-1 text-white/60 bouncy mb-4" data-testid="plan-back-btn">
          <ChevronLeft size={18} /> {t("Home")}
        </button>
        <div className="flex items-center gap-2 mb-1">
          <Sparkles style={{ color: "var(--violet)" }} />
          <h1 className="font-heading font-black text-3xl tracking-tight">{t("Piano da Appunti")}</h1>
        </div>
        <p className="text-white/60 mb-6 text-sm">{t("Carica appunti o fonti: l'AI crea un piano, quiz e livelli su misura — includendo le basi necessarie per capire gli argomenti.")}</p>

        <div className="glass rounded-3xl p-5 space-y-4">
          <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder={t("Titolo (es. Aerodinamica - Cap. 3)")}
            className="w-full bg-black/30 rounded-2xl px-4 py-3 outline-none placeholder-white/40" data-testid="plan-title-input" />

          <textarea value={text} onChange={(e) => setText(e.target.value)} rows={5}
            placeholder={t("Incolla qui i tuoi appunti o argomenti da studiare...")}
            className="w-full bg-black/30 rounded-2xl px-4 py-3 outline-none placeholder-white/40 resize-none" data-testid="plan-text-input" />

          <label className="flex items-center justify-center gap-2 border-2 border-dashed border-white/20 rounded-2xl py-5 cursor-pointer bouncy hover:border-white/40" data-testid="plan-file-label">
            <Upload size={20} style={{ color: "var(--cyan)" }} />
            <span className="text-sm text-white/70">{t("Carica PDF, testo o foto degli appunti")}</span>
            <input type="file" multiple accept=".pdf,.txt,.md,.csv,image/*" onChange={onFiles} className="hidden" data-testid="plan-file-input" />
          </label>

          {files.length > 0 && (
            <div className="space-y-2" data-testid="plan-file-list">
              {files.map((f, i) => (
                <div key={i} className="flex items-center gap-2 bg-black/30 rounded-xl px-3 py-2 text-sm">
                  <FileText size={16} className="text-white/60" />
                  <span className="flex-1 truncate">{f.name}</span>
                  <button onClick={() => removeFile(i)} className="text-white/50 hover:text-red-400"><X size={16} /></button>
                </div>
              ))}
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div className="bg-black/30 rounded-2xl px-4 py-3">
              <label className="text-xs text-white/50 flex items-center gap-1"><CalendarDays size={12} /> {t("Giorni")}</label>
              <input type="number" min={1} max={60} value={days} onChange={(e) => setDays(+e.target.value)}
                className="w-full bg-transparent outline-none font-num text-xl" data-testid="plan-days-input" />
            </div>
            <div className="bg-black/30 rounded-2xl px-4 py-3">
              <label className="text-xs text-white/50 flex items-center gap-1"><Clock size={12} /> {t("Ore/giorno")}</label>
              <input type="number" min={0.5} step={0.5} max={12} value={hours} onChange={(e) => setHours(+e.target.value)}
                className="w-full bg-transparent outline-none font-num text-xl" data-testid="plan-hours-input" />
            </div>
          </div>

          <button onClick={submit} disabled={busy} className="w-full py-4 rounded-2xl btn-violet font-heading font-bold bouncy disabled:opacity-60" data-testid="plan-generate-btn">
            {busy ? t("L'AI sta creando il piano...") : t("✨ Genera piano di studio")}
          </button>
        </div>
      </div>
    </div>
  );
}
